import { createAxiosInstance } from "./apiUtils";

export type ReferenceOption = { value: string; label?: string | null; description?: string | null; group?: string | null };
export type ReferenceDataResponse = { options: ReferenceOption[]; cache_ttl_seconds?: number; truncated?: boolean };

type CacheEntry = { expiresAt: number; promise: Promise<ReferenceDataResponse> };
const cache = new Map<string, CacheEntry>();

export function clearReferenceOptionsCache() { cache.clear(); }  // tests

export async function fetchReferenceOptions(
    token: string, integrationId: string, actionValue: string, params: Record<string, unknown>,
): Promise<ReferenceDataResponse> {
    const key = JSON.stringify([integrationId, actionValue, params]);
    const hit = cache.get(key);
    if (hit && hit.expiresAt > Date.now()) return hit.promise;   // fresh or in-flight
    const promise = (async () => {
        const axiosInstance = await createAxiosInstance(token);
        const resp = await axiosInstance.post(
            `/v2/integrations/${encodeURIComponent(integrationId)}/actions/${encodeURIComponent(actionValue)}/execute/`,
            { config_overrides: params },
            { timeout: 15000 },  // Cloud Run cold starts: generous, behind a spinner
        );
        const data: ReferenceDataResponse = resp.data;
        const ttlMs = (data.cache_ttl_seconds ?? 300) * 1000;
        cache.set(key, { expiresAt: Date.now() + ttlMs, promise: Promise.resolve(data) });
        return data;
    })();
    // In-flight dedupe: cache immediately with a short provisional TTL; on
    // failure evict so the next open retries.
    cache.set(key, { expiresAt: Date.now() + 15000, promise });
    promise.catch(() => { if (cache.get(key)?.promise === promise) cache.delete(key); });
    return promise;
}
